import { supabase } from '../auth/supabase-client';
import { clientEnvironment } from '../config/client-env';
import { declencherPush } from '../notifications/push';
import type {
  DecisionContexte,
  PropositionDecision,
} from '../../supabase/functions/_shared/prompts/decision.ts';
import type {
  VoiceContext,
  VoiceExtraction,
} from '../../supabase/functions/_shared/prompts/voice-contract.ts';
import type { Json } from '../types/database.generated';
import { creerListe } from './listes';
import { creerTache } from './taches';
import type { CheckRhythm, Decision, DecisionKind, Liste, Tache } from './types';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

/** Vrai quand le build demande des propositions IA déterministes (tests, démo). */
const modeFixtureIA = clientEnvironment.VITE_ALBA_AI_FIXTURE === 'true';

// --- Soumission et décision ---------------------------------------------------

export async function soumettreDecision(
  tacheId: string,
  deciderId?: string | null,
): Promise<Tache> {
  const { data, error } = await client().rpc('soumettre_decision', {
    p_task: tacheId,
    ...(deciderId ? { p_decider: deciderId } : {}),
  });
  if (error) throw error;
  return data;
}

export async function decideurParDefaut(listeId: string): Promise<string | null> {
  const { data, error } = await client().rpc('decideur_par_defaut', { p_list: listeId });
  if (error) throw error;
  return data ?? null;
}

export type ChoixDecision = {
  kind: DecisionKind;
  assigneeId?: string | null;
  dueDate?: string | null;
  rythme?: CheckRhythm | null;
  montantCents?: number | null;
  note?: string | null;
  proposition?: PropositionDecision | null;
  propositionAcceptee?: boolean;
  enReunion?: boolean;
};

export async function enregistrerDecision(
  tacheId: string,
  choix: ChoixDecision,
): Promise<Decision> {
  const { data, error } = await client().rpc('decider', {
    p_task: tacheId,
    p_kind: choix.kind,
    p_rhythm: choix.rythme ?? 'none',
    p_ai_accepted: choix.propositionAcceptee ?? false,
    p_in_meeting: choix.enReunion ?? false,
    ...(choix.assigneeId ? { p_assignee: choix.assigneeId } : {}),
    ...(choix.dueDate ? { p_due: choix.dueDate } : {}),
    ...(choix.montantCents !== null && choix.montantCents !== undefined
      ? { p_amount_cents: choix.montantCents }
      : {}),
    ...(choix.note ? { p_note: choix.note } : {}),
    ...(choix.proposition ? { p_ai_proposal: choix.proposition as unknown as Json } : {}),
  });
  if (error) throw error;
  // Le demandeur et la personne à qui la tâche revient sont prévenus tout de
  // suite : sans cet appel, leur push attendrait le prochain tour de cron.
  declencherPush();
  return data;
}

export async function marquerPoint(
  tacheId: string,
  evolution: boolean,
  note?: string | null,
): Promise<Tache> {
  const { data, error } = await client().rpc('marquer_point', {
    p_task: tacheId,
    p_evolution: evolution,
    ...(note ? { p_note: note } : {}),
  });
  if (error) throw error;
  return data;
}

// --- Cartes à décider ------------------------------------------------------------

/** Toutes les tâches en attente de décision visibles dans l'espace, les plus anciennes d'abord. */
export async function chargerCartes(workspaceId: string): Promise<Tache[]> {
  const { data, error } = await client()
    .from('tasks')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('status', 'attente_decision')
    .order('decision_requested_at', { ascending: true, nullsFirst: false })
    .order('created_at');
  if (error) throw error;
  return data ?? [];
}

/** Nombre de cartes qui attendent la personne connectée dans cet espace. */
export type DecisionRecente = Decision & {
  tasks: Pick<Tache, 'id' | 'title' | 'list_id' | 'status' | 'owner_member_id'>;
};

/**
 * Les décisions rendues dans l'espace depuis une date, les plus récentes
 * d'abord : une carte décidée ne disparaît pas, elle se retrouve ici.
 */
export async function chargerDecisionsRecentes(
  workspaceId: string,
  depuis: string,
  limite = 30,
): Promise<DecisionRecente[]> {
  const { data, error } = await client()
    .from('decisions')
    .select('*, tasks!inner(id, title, list_id, status, owner_member_id, workspace_id)')
    .eq('tasks.workspace_id', workspaceId)
    .gte('created_at', depuis)
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data ?? [];
}

export async function compterCartesPourMoi(
  workspaceId: string,
  monMembreId: string,
): Promise<number> {
  const { count, error } = await client()
    .from('tasks')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('status', 'attente_decision')
    .eq('decider_member_id', monMembreId)
    .is('quote_task_id', null);
  if (error) throw error;
  return count ?? 0;
}

// --- Proposition IA ------------------------------------------------------------

export type CartePreparee = {
  proposal: PropositionDecision;
  context: DecisionContexte;
  cached: boolean;
  fixture: boolean;
  fallback: boolean;
  model: string | null;
  generated_at: string | null;
};

export async function preparerCarte(tacheId: string, force = false): Promise<CartePreparee> {
  const { data, error } = (await client().functions.invoke('alba-decision-prepare', {
    body: { task_id: tacheId, force, fixture: modeFixtureIA },
  })) as { data: CartePreparee | null; error: unknown };
  if (error)
    throw new Error(await messageErreurFonction(error, 'La proposition IA est indisponible.'));
  if (!data?.proposal) throw new Error('Proposition IA vide.');
  return data;
}

// --- Dictée vocale ---------------------------------------------------------------

export type Dictee = {
  transcript: string;
  extraction: VoiceExtraction;
  fixture: boolean;
  fallback: boolean;
};

export async function dicter(
  entree: { audio?: Blob | null; transcript?: string | null },
  contexte: VoiceContext,
): Promise<Dictee> {
  let corps: FormData | Record<string, unknown>;
  if (entree.audio) {
    const formulaire = new FormData();
    formulaire.append('audio', entree.audio, 'dictee');
    if (entree.transcript) formulaire.append('transcript', entree.transcript);
    formulaire.append('context', JSON.stringify(contexte));
    if (modeFixtureIA) formulaire.append('fixture', 'true');
    corps = formulaire;
  } else {
    corps = { transcript: entree.transcript ?? '', context: contexte, fixture: modeFixtureIA };
  }
  const { data, error } = (await client().functions.invoke('alba-voice', { body: corps })) as {
    data: Dictee | null;
    error: unknown;
  };
  if (error) throw new Error(await messageErreurFonction(error, 'La dictée n’a pas abouti.'));
  if (!data?.extraction) throw new Error('Dictée vide.');
  return data;
}

/** Le message d'erreur porté par la réponse d'une edge function, sinon un mot simple. */
export async function messageErreurFonction(erreur: unknown, defaut: string): Promise<string> {
  const contexte = (erreur as { context?: Response }).context;
  if (contexte && typeof contexte.json === 'function') {
    try {
      const charge = (await contexte.json()) as { error?: string };
      if (charge.error) return charge.error;
    } catch {
      // Réponse sans JSON : on garde le message générique.
    }
  }
  return (erreur as Error).message || defaut;
}

// --- Journal de réunion --------------------------------------------------------------

const LISTE_REUNIONS = 'Réunions';

export type JournalReunion = {
  workspaceId: string;
  poleId: string | null;
  monMembreId: string | null;
  titre: string;
  texte: string;
  listes: Liste[];
};

/** Enregistre le journal d'une séance dans la liste « Réunions » de l'espace (créée au besoin). */
export async function enregistrerJournalReunion(journal: JournalReunion): Promise<Tache> {
  const kind = journal.poleId ? 'pole' : 'direction';
  let liste = journal.listes.find(
    (l) =>
      !l.archived &&
      l.name === LISTE_REUNIONS &&
      l.kind === kind &&
      (journal.poleId ? l.pole_id === journal.poleId : true),
  );
  if (!liste) {
    liste = await creerListe(
      journal.workspaceId,
      { name: LISTE_REUNIONS, kind, pole_id: journal.poleId, color: 'ardoise' },
      journal.listes.length,
    );
  }
  const tache = await creerTache({
    id: crypto.randomUUID(),
    workspaceId: journal.workspaceId,
    listId: liste.id,
    title: journal.titre,
    position: 0,
    createdBy: journal.monMembreId,
  });
  const { data, error } = await client()
    .from('tasks')
    .update({ description: journal.texte, status: 'fait' })
    .eq('id', tache.id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}
